import { HUD, PIXEL_FACES } from "odyssey-shared";

/**
 * Where the loading screen's parts stand on an area (P1.4-T5; ADR-061's amendment of 2026-10-06). On the design's
 * 640 by 360 grid, and on any taller area, the art band is 216 tall with its line, the column holding the bar block
 * hangs 14 under the band at 230, and a failure's notice stands at 110. On a shorter area the band gives way so the
 * bar block keeps the same 14 under itself that it has above, and the notice rises to keep the HUD's margin under it,
 * so the bar and the notice's button are whole on the page down to the smallest area the layouts hold. Pure: the
 * screen measures and hands the sizes in.
 */

/**
 * The screen's measurements in logical pixels: the art band's height, which holds its one-pixel bottom line, the
 * column's top and width, the bar's width, the notice's top and width (all from the design update's
 * layout), and the wordmark, the title face at its measured size and line.
 */
export const LOADING_METRICS = {
  art: 216,
  artLine: 1,
  column: { top: 230, width: 400 },
  bar: 240,
  notice: { top: 110, width: 260 },
  wordmark: { size: PIXEL_FACES.title.nativeSize, lineHeight: PIXEL_FACES.title.lineHeight },
} as const;

/**
 * The gap between the band and the column, which the column also keeps under itself on a short area.
 */
const COLUMN_GAP = LOADING_METRICS.column.top - LOADING_METRICS.art;

/**
 * Where the band ends, the column starts and the notice starts, from the area's top in logical pixels.
 */
export interface LoadingPlaces {
  readonly art: number;
  readonly columnTop: number;
  readonly noticeTop: number;
}

/**
 * The design's places, which every area as tall as the grid takes and a screen not yet measured draws.
 */
export const LOADING_PLACES: LoadingPlaces = { art: LOADING_METRICS.art, columnTop: LOADING_METRICS.column.top, noticeTop: LOADING_METRICS.notice.top };

/**
 * The parts' places on an area.
 *
 * @param areaHeight The screen's height in logical pixels, or null before it is measured.
 * @param columnHeight The bar block's height in logical pixels, or null while there is none.
 * @param noticeHeight The notice's height in logical pixels, or null while there is none.
 * @returns The band's height, never more than the design's 216 and never less than the wordmark's line, so the
 *   column, its gap under the band and the same gap under itself fit the area; the column's top under the band;
 *   and the notice's top, the design's 110 or as much higher as keeps the HUD's margin under the notice,
 *   and never above that margin. The design's places before the area is measured.
 * @example
 * loadingPlaces(230, 27, null);
 * // { art: 175, columnTop: 189, noticeTop: 110 }
 */
export function loadingPlaces(areaHeight: number | null, columnHeight: number | null, noticeHeight: number | null): LoadingPlaces {
  if (areaHeight === null) {
    return LOADING_PLACES;
  }
  const area = Math.floor(areaHeight);
  const wanted = columnHeight === null ? LOADING_METRICS.art : area - Math.ceil(columnHeight) - 2 * COLUMN_GAP;
  const art = Math.max(LOADING_METRICS.wordmark.lineHeight, Math.min(LOADING_METRICS.art, wanted));
  const noticeTop = noticeHeight === null ? LOADING_METRICS.notice.top : Math.max(HUD.margin, Math.min(LOADING_METRICS.notice.top, area - Math.ceil(noticeHeight) - HUD.margin));
  return { art, columnTop: art + COLUMN_GAP, noticeTop };
}
